import { type ReactNode, useCallback } from 'react';

import { useEventListener } from 'ovpn-ui-toolkit';

export const eventToast = 'ovpn:on-toast';

type ToastOptions = {
    closeButton?: boolean;

    /**
     * Set the delay in ms to close the toast automatically.
     * Use `false` to prevent the toast from closing.
     * `Default: 5000`
     */
    autoClose?: number | false;
};

const ToastType = {
    Error: 'error',
    Success: 'success',
    Dismiss: 'dismiss',
    Info: 'info',
};

type Toaster = {
    success: (msg: ReactNode, opts?: ToastOptions) => void;
    error: (msg: ReactNode, opts?: ToastOptions) => void;
    info: (msg: ReactNode, opts?: ToastOptions) => void;
    dismiss(id?: string | number | undefined): void;
};

/**
 * Removes duplicated messages
 *
 * @param rawMessages all messages received from the backend combined as a single message
 * @returns messages without duplicates
 */
const extractUniqueMessages = (rawMessages: string | JSX.Element) => {
    if (typeof rawMessages === 'string') {
        const messages = rawMessages.split('\n');
        const uniqueMessages = [...new Set([...messages])].join('\n');
        return uniqueMessages;
    }

    // means toast message is some React Element, which is allowed.
    return rawMessages;
};

/**
 * Listen for toast window events from child MFEs.
 *
 * @param toasterInstance toast library facade instance that should support success and error methods.
 *
 * p.s. we pass toaser instead of using it inside directly to abstract away from specific toast lib (IoC)
 */
function useToastListener(toasterInstance: Toaster) {
    const handleToast = useCallback(
        (e: CustomEvent<{ type: string; data: any; options: ToastOptions }>) => {
            const { type, data, options = {} } = e.detail;

            switch (type) {
                case ToastType.Dismiss: {
                    toasterInstance.dismiss(data);
                    break;
                }

                case ToastType.Error: {
                    if (data) {
                        toasterInstance.error(extractUniqueMessages(data), options);
                    }
                    break;
                }

                case ToastType.Success: {
                    if (data) {
                        toasterInstance.success(data, options);
                    }
                    break;
                }

                case ToastType.Info: {
                    if (data) {
                        toasterInstance.info(data, options);
                    }
                    break;
                }

                default: {
                    console.warn('[cloud-nav] unknown toast event: ', { type, data });
                }
            }
        },
        [toasterInstance],
    );

    // @ts-expect-error useEventListener doesn't support custom events
    useEventListener(eventToast, handleToast);
}

const dispatchToastEvent = (eventType: any, eventData: any, options?: any) =>
    window.dispatchEvent(
        new CustomEvent(eventToast, {
            detail: {
                type: eventType,
                data: eventData,
                options: {
                    closeButton: options?.closeButton ?? false,
                    autoClose: options?.autoClose ?? undefined,
                },
            },
        }),
    );

const pushToastByType = (type: string) => (toast: ReactNode, opts?: ToastOptions) => {
    dispatchToastEvent(type, toast, opts);
};

const toast: Toaster = {
    error: pushToastByType(ToastType.Error),
    success: pushToastByType(ToastType.Success),
    info: pushToastByType(ToastType.Info),
    dismiss: (id?: string | number | undefined) => dispatchToastEvent(ToastType.Dismiss, id),
};

export { toast, useToastListener };
